import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  MdCloudUpload,
  MdOutlineVideoLibrary,
  MdCheckCircle,
  MdClose,
  MdFileUpload,
  MdOutlineInfo,
  MdShare,
  MdSmartDisplay,
  MdNotificationsActive,
} from 'react-icons/md';
import { RiShareForwardFill } from 'react-icons/ri';
import { getAvatarColor } from './utils/avatarUtils';
import { getActiveAccount } from './utils/accountUtils';
import { fetchChannelCustomization, fetchLiveSubscribers } from './api/studioApi';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import SignInPromptModal from './SignInPromptModal';
import VideoCard, { DEFAULT_VIDEO_THUMBNAIL } from './VideoCard';
import './ChannelPage.css';
import './VideoGrid.css';

// Helper to format duration seconds to mm:ss or hh:mm:ss
function formatDuration(sec) {
  if (!sec && sec !== 0) return '0:00';
  const total = parseInt(sec, 10);
  if (isNaN(total)) return String(sec);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const padSec = seconds < 10 ? `0${seconds}` : `${seconds}`;
  if (hours > 0) {
    const padMin = minutes < 10 ? `0${minutes}` : `${minutes}`;
    return `${hours}:${padMin}:${padSec}`;
  }
  return `${minutes}:${padSec}`;
}

// Helper to format relative time
function formatRelativeTime(dateStr) {
  if (!dateStr) return 'Recently';
  const date = new Date(dateStr);
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
}

// Helper to format subscriber count
function formatSubscribers(count) {
  if (!count && count !== 0) return '0 subscribers';
  const num = parseInt(count, 10);
  if (isNaN(num) || num <= 0) return '0 subscribers';
  if (num === 1) return '1 subscriber';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M subscribers`;
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K subscribers`;
  return `${num} subscribers`;
}

export default function ChannelPage({ userProfile, onNavigate, channelHandle, isLoggedIn, onLogin, onOpenUpload }) {
  const isUserAuthenticated = useMemo(() => {
    if (typeof isLoggedIn === 'boolean') return isLoggedIn;
    if (typeof window !== 'undefined' && localStorage.getItem('speedotube_logged_out') === 'true') {
      return false;
    }
    if (userProfile && (userProfile.id || userProfile.token || userProfile.userId)) {
      return true;
    }
    const token = typeof window !== 'undefined' ? localStorage.getItem('speedotube_user_token') : null;
    const account = typeof window !== 'undefined' ? localStorage.getItem('speedotube_user_account') : null;
    return Boolean(token && account && localStorage.getItem('speedotube_logged_out') !== 'true');
  }, [isLoggedIn, userProfile]);

  const [signInPrompt, setSignInPrompt] = useState({
    isOpen: false,
    action: 'general',
    targetName: '',
  });

  const [activeAcc, setActiveAcc] = useState(getActiveAccount());
  const [channelData, setChannelData] = useState(() => {
    try {
      const handle = userProfile?.handle || getActiveAccount()?.handle || (userProfile?.name ? `@${userProfile.name.toLowerCase().replace(/[^a-z0-9]/gi, '')}` : null);
      if (handle) {
        const cached = localStorage.getItem(`speedotube_customization_${handle.toLowerCase()}`);
        if (cached) return JSON.parse(cached);
      }
    } catch (e) {}
    return null;
  });

  const [targetChannel, setTargetChannel] = useState(null);
  const [channelVideos, setChannelVideos] = useState([]);
  const [channelShorts, setChannelShorts] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribersCount, setSubscribersCount] = useState(0);
  const [subAnimType, setSubAnimType] = useState(null); // 'subscribing' | 'unsubscribing' | null
  const [subAnimKey, setSubAnimKey] = useState(0);
  const subAnimTimerRef = useRef(null);

  const triggerSubAnim = useCallback((willBeSubscribed) => {
    if (subAnimTimerRef.current) clearTimeout(subAnimTimerRef.current);
    setSubAnimType(willBeSubscribed ? 'subscribing' : 'unsubscribing');
    setSubAnimKey((prev) => prev + 1);
    subAnimTimerRef.current = setTimeout(() => {
      setSubAnimType(null);
    }, 380);
  }, []);

  const [activeTab, setActiveTab] = useState('videos');

  const cleanTarget = (channelHandle || '').trim().toLowerCase().replace(/^@/, '');
  const currentAcc = userProfile || activeAcc || getActiveAccount();
  const myHandle = (currentAcc?.handle || '').trim().toLowerCase().replace(/^@/, '');
  const myName = (currentAcc?.name || currentAcc?.fullName || '').trim().toLowerCase();
  const myCleanName = myName.replace(/[^a-z0-9]/gi, '');
  const myId = String(currentAcc?.channelId || currentAcc?.channel_id || '');
  const myUserId = String(currentAcc?.userId || currentAcc?.id || '');
  const myLegacyId = String(currentAcc?.legacyId || '');

  const isAukatTarget = cleanTarget.includes('aukat') || cleanTarget === '40' || cleanTarget.includes('explorer');
  const isMyAukat = myHandle.includes('aukat') || myName.includes('aukat') || myId === '40' || myUserId === '198' || myLegacyId === '3';

  const isPrathuTarget = cleanTarget.includes('prath') || cleanTarget === '38';
  const isMyPrathu = myHandle.includes('prath') || myName.includes('prath') || myId === '38' || myUserId === '147' || myLegacyId === '1';

  const isOwnChannel = isUserAuthenticated && (!channelHandle || 
    (myHandle && cleanTarget === myHandle) || 
    (myName && cleanTarget === myName) || 
    (myCleanName && cleanTarget === myCleanName) || 
    (myId && cleanTarget === myId) ||
    (isAukatTarget && isMyAukat) ||
    (isPrathuTarget && isMyPrathu));

  // Listen for real-time account switch or channel customization updates
  React.useEffect(() => {
    const handleAccountChange = (e) => {
      const updated = e.detail || getActiveAccount();
      setActiveAcc(updated);
      if (e.detail) {
        const ch = e.detail.channel || e.detail;
        setChannelData((prev) => ({
          ...(prev || {}),
          ...ch,
        }));
        if (ch?.subscribers !== undefined) {
          setSubscribersCount(Number(ch.subscribers));
        }
      }
    };
    window.addEventListener('speedotube_user_switched', handleAccountChange);
    window.addEventListener('speedotube_channel_updated', handleAccountChange);
    return () => {
      window.removeEventListener('speedotube_user_switched', handleAccountChange);
      window.removeEventListener('speedotube_channel_updated', handleAccountChange);
    };
  }, []);

  // Fetch target/own channel data, live subscribers count, and channel videos
  useEffect(() => {
    const effectiveHandle = channelHandle || userProfile?.handle || activeAcc?.handle || null;
    if (!effectiveHandle) return;
    const normalizedHandle = effectiveHandle.startsWith('@') ? effectiveHandle : `@${effectiveHandle}`;
    const active = getActiveAccount();
    const isLoggedOut = typeof window !== 'undefined' && localStorage.getItem('speedotube_logged_out') === 'true';
    const token = (!isLoggedOut && isUserAuthenticated) ? (active?.token || localStorage.getItem('speedotube_user_token')) : null;

    const authHeaders = {
      token: 'c7e993808a344da9d59cfa20fa8b6061',
      devicetype: '0',
      request_type: 'USER',
    };
    if (token) {
      authHeaders.authorization = `Bearer ${token}`;
    }

    // 1. Fetch official channel profile from /api/v1/channels/:handle_name
    fetch(`${API_BASE_URL}/api/v1/channels/${encodeURIComponent(normalizedHandle)}`, {
      headers: authHeaders,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const ch = json?.data?.channel;
        if (ch) {
          if (!isOwnChannel) {
            setTargetChannel((prev) => ({ ...(prev || {}), ...ch }));
            if (isUserAuthenticated && token && typeof ch.isUserSubscribed === 'boolean') {
              setIsSubscribed(Boolean(ch.isUserSubscribed));
            }
          } else {
            setChannelData((prev) => ({ ...(prev || {}), ...ch }));
          }
          if (ch.subscribers !== undefined && !isNaN(Number(ch.subscribers))) {
            setSubscribersCount(Number(ch.subscribers));
          }
        }
      })
      .catch((err) => {
        console.warn('Failed to fetch channel from /api/v1/channels/:handle:', err);
      });

    // 1b. Fetch channel customization (custom links, trailer, home tab) to merge
    fetchChannelCustomization(null, normalizedHandle).then((data) => {
      if (data) {
        if (!isOwnChannel) {
          setTargetChannel((prev) => ({ ...(prev || {}), ...data }));
          if (isUserAuthenticated && token && typeof data.isUserSubscribed === 'boolean') {
            setIsSubscribed(Boolean(data.isUserSubscribed));
          }
        } else {
          setChannelData((prev) => ({ ...(prev || {}), ...data }));
        }
        if (data.subscribers !== undefined && !isNaN(Number(data.subscribers))) {
          setSubscribersCount(Number(data.subscribers));
        }
      }
    });

    // 2. Query live subscriber count from backend API
    fetchLiveSubscribers(null, normalizedHandle).then((count) => {
      if (count !== undefined && count !== null && !isNaN(Number(count))) {
        setSubscribersCount(Number(count));
      }
    });

    // 3. Fetch channel's videos (regular)
    fetch(`${API_BASE_URL}/api/v1/videos/channels/${encodeURIComponent(normalizedHandle)}?limit=50`, {
      headers: {
        token: 'c7e993808a344da9d59cfa20fa8b6061',
        devicetype: '0',
        request_type: 'USER',
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (json?.data?.videos) {
          const mapped = json.data.videos.map((vid) => ({
            ...vid,
            thumbnail_url: normalizeMediaUrl(vid.thumbnail_url) || DEFAULT_VIDEO_THUMBNAIL,
          }));
          setChannelVideos(mapped);
          if (mapped.length > 0 && mapped[0].channel_id) {
            setTargetChannel((prev) => (prev ? { ...prev, id: prev.id || mapped[0].channel_id } : { id: mapped[0].channel_id }));
          }
        } else {
          setChannelVideos([]);
        }
      })
      .catch((err) => {
        console.warn('Failed to fetch channel videos:', err);
        setChannelVideos([]);
      });

    // 3b. Fetch channel's shorts (vertical)
    fetch(`${API_BASE_URL}/api/v1/shorts/channels/${encodeURIComponent(normalizedHandle)}?limit=50`, {
      headers: {
        token: 'c7e993808a344da9d59cfa20fa8b6061',
        devicetype: '0',
        request_type: 'USER',
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const rawShorts = json?.data?.videos || json?.data?.shorts || [];
        const mapped = rawShorts.map((s) => {
          const rawThumb = s.thumbnail_url || (s.thumbnail_destination ? `${API_BASE_URL}${s.thumbnail_destination}` : null) || DEFAULT_VIDEO_THUMBNAIL;
          return {
            ...s,
            thumbnail_url: normalizeMediaUrl(rawThumb),
          };
        });
        setChannelShorts(mapped);
        if (mapped.length > 0) {
          setActiveTab((prev) => (prev === 'videos' ? 'shorts' : prev));
          if (mapped[0].channel_id) {
            setTargetChannel((prev) => (prev ? { ...prev, id: prev.id || mapped[0].channel_id } : { id: mapped[0].channel_id }));
          }
        }
      })
      .catch((err) => {
        console.warn('Failed to fetch channel shorts:', err);
        setChannelShorts([]);
      });

    // 4. If viewing another channel, check if logged-in user is subscribed to this channel
    if (!isOwnChannel) {
      if (isUserAuthenticated && token) {
        fetch(`${API_BASE_URL}/api/v1/subscribed/channels?limit=50`, {
          headers: {
            token: 'c7e993808a344da9d59cfa20fa8b6061',
            devicetype: '0',
            request_type: 'USER',
            authorization: `Bearer ${token}`,
          },
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((json) => {
            const list = json?.data?.channels || [];
            const cleanTarget = normalizedHandle.toLowerCase().replace(/^@/, '');
            const found = list.find((c) => {
              const cHandle = c.handle_name?.toLowerCase().replace(/^@/, '');
              const cName = c.name?.toLowerCase().trim();
              const cId = String(c.channel_id);
              return cHandle === cleanTarget || cName === cleanTarget || cId === String(channelHandle) || (targetChannel?.id && cId === String(targetChannel.id));
            });
            if (found) {
              setIsSubscribed(true);
            }
          })
          .catch(() => {});
      } else {
        setIsSubscribed(false);
      }
    }
  }, [channelHandle, isOwnChannel, userProfile?.handle, activeAcc?.handle, activeAcc?.token, isUserAuthenticated]);

  // Real-time subscriber count & subscription state synchronization
  useEffect(() => {
    const handleSubToggled = (e) => {
      if (!isUserAuthenticated) {
        setIsSubscribed(false);
        return;
      }
      const currentChId = targetChannel?.id || targetChannel?.channel_id || channelVideos?.[0]?.channel_id || channelShorts?.[0]?.channel_id;
      if (e?.detail?.channelId && currentChId && String(e.detail.channelId) === String(currentChId)) {
        if (typeof e.detail.isSubscribed === 'boolean') {
          setIsSubscribed(e.detail.isSubscribed);
        }
      }
      const effectiveHandle = channelHandle || userProfile?.handle || activeAcc?.handle || null;
      if (!effectiveHandle) return;
      const normalizedHandle = effectiveHandle.startsWith('@') ? effectiveHandle : `@${effectiveHandle}`;
      fetchLiveSubscribers(null, normalizedHandle).then((count) => {
        if (count !== undefined && count !== null && !isNaN(Number(count))) {
          setSubscribersCount(Number(count));
        }
      });
    };
    window.addEventListener('speedotube_subscription_toggled', handleSubToggled);
    window.addEventListener('speedotube:subscription-changed', handleSubToggled);
    return () => {
      window.removeEventListener('speedotube_subscription_toggled', handleSubToggled);
      window.removeEventListener('speedotube:subscription-changed', handleSubToggled);
    };
  }, [targetChannel?.id, targetChannel?.channel_id, channelVideos, channelShorts, channelHandle, userProfile?.handle, activeAcc?.handle, isUserAuthenticated]);

  // Subscription toggle handler
  const handleSubscribeToggle = async () => {
    if (!isUserAuthenticated) {
      setSignInPrompt({
        isOpen: true,
        action: 'subscribe',
        targetName: profileName || 'this channel',
      });
      return;
    }
    if (isOwnChannel) {
      return;
    }

    const active = getActiveAccount();
    const isLoggedOut = typeof window !== 'undefined' && localStorage.getItem('speedotube_logged_out') === 'true';
    const token = (!isLoggedOut && isUserAuthenticated) ? (active?.token || localStorage.getItem('speedotube_user_token')) : null;
    if (!token) {
      setSignInPrompt({
        isOpen: true,
        action: 'subscribe',
        targetName: profileName || 'this channel',
      });
      return;
    }

    const cleanTgt = (channelHandle || '').trim().toLowerCase().replace(/^@/, '');
    const chId = targetChannel?.id || targetChannel?.channel_id || 
                 (cleanTgt === 'pratham' || cleanTgt === 'prathu' ? 38 : null) ||
                 (cleanTgt === 'aukatdikhadi' || cleanTgt === 'speedoexplorer' ? 40 : null) ||
                 channelVideos?.[0]?.channel_id || channelShorts?.[0]?.channel_id;
    if (!chId) {
      console.warn('Cannot toggle subscription: channel ID missing');
      return;
    }

    const myChId = active?.channelId || active?.channel_id || userProfile?.channelId;
    if (String(myChId) === String(chId) || isOwnChannel) {
      return;
    }

    const nextSubscribed = !isSubscribed;
    triggerSubAnim(nextSubscribed);
    setIsSubscribed(nextSubscribed);
    setSubscribersCount((prev) => (nextSubscribed ? prev + 1 : Math.max(0, prev - 1)));

    const headers = {
      token: 'c7e993808a344da9d59cfa20fa8b6061',
      devicetype: '0',
      request_type: 'USER',
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    };

    try {
      const endpoint = nextSubscribed
        ? `${API_BASE_URL}/api/v1/subscribers/subscribe/channels/${chId}`
        : `${API_BASE_URL}/api/v1/subscribers/unsubscribe/channels/${chId}`;

      const postBody = {};
      if (channelVideos?.[0]?.id) {
        postBody.videoId = Number(channelVideos[0].id);
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(postBody),
      });

      if (!res.ok) {
        throw new Error('Subscription toggle request failed');
      }

      const resJson = await res.json();
      const newCount = resJson?.data?.subscribe?.subscribers_count ?? resJson?.data?.unsubscribe?.subscribers_count;
      if (newCount !== undefined && !isNaN(Number(newCount))) {
        setSubscribersCount(Number(newCount));
      }

      setIsSubscribed(nextSubscribed);

      window.dispatchEvent(
        new CustomEvent('speedotube_subscription_toggled', {
          detail: { channelId: chId, isSubscribed: nextSubscribed },
        })
      );
      window.dispatchEvent(
        new CustomEvent('speedotube:subscription-changed', {
          detail: { channelId: chId, isSubscribed: nextSubscribed },
        })
      );
    } catch (e) {
      console.warn('Subscription toggle failed:', e);
      setIsSubscribed(!nextSubscribed);
      setSubscribersCount((prev) => (nextSubscribed ? Math.max(0, prev - 1) : prev + 1));
    }
  };

  const currentChannel = !isOwnChannel ? targetChannel : channelData;

  const rawTargetName = targetChannel?.name;
  const rawChannelDataName = channelData?.name;

  const profileName = !isOwnChannel
    ? (rawTargetName || channelHandle?.replace('@', '') || 'Channel')
    : (rawChannelDataName || userProfile?.name || activeAcc?.name || activeAcc?.fullName || 'User');

  const profileHandle = !isOwnChannel
    ? (targetChannel?.handle_name || (channelHandle?.startsWith('@') ? channelHandle : `@${channelHandle}`))
    : (channelData?.handle_name || userProfile?.handle || activeAcc?.handle || (profileName ? `@${profileName.toLowerCase().replace(/[^a-z0-9]/gi, '')}` : '@user'));

  const profileInitial = (profileName && profileName.trim()) ? profileName.trim().charAt(0).toUpperCase() : 'U';
  const avatarColor = getAvatarColor(profileName || profileHandle);

  const channelBanner = normalizeMediaUrl(
    currentChannel !== null
      ? (currentChannel?.banner_url || currentChannel?.banner_filename || '')
      : (userProfile?.banner_url || activeAcc?.banner_url || '')
  );

  const channelAvatar = normalizeMediaUrl(
    currentChannel !== null
      ? (currentChannel?.avatar_url || currentChannel?.avatar_filename || '')
      : (userProfile?.avatar_url || userProfile?.profile_image || activeAcc?.avatar_url || activeAcc?.profile_image || '')
  );

  // Synchronize browser tab title dynamically with channel name
  useEffect(() => {
    if (profileName) {
      document.title = `${profileName} - SpeedoTube`;
    }
  }, [profileName]);

  // Adaptive Banner Contrast Analysis (automatically senses dark vs bright banner BG)
  const [bannerTheme, setBannerTheme] = useState('dark');

  useEffect(() => {
    if (!channelBanner) {
      setBannerTheme('default');
      return;
    }
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = channelBanner;
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 64;
        canvas.height = 64;
        const ctx = canvas.getContext('2d');
        // Sample left 50% where avatar and text sit
        ctx.drawImage(img, 0, 0, img.naturalWidth * 0.5, img.naturalHeight, 0, 0, 64, 64);
        const data = ctx.getImageData(0, 0, 64, 64).data;
        let totalLum = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
          totalLum += lum;
          count++;
        }
        const avgLum = count > 0 ? totalLum / count : 0.4;
        setBannerTheme(avgLum < 0.55 ? 'dark' : 'light');
      } catch (e) {
        setBannerTheme('dark');
      }
    };
    img.onerror = () => setBannerTheme('dark');
  }, [channelBanner]);

  React.useEffect(() => {
    if (!isOwnChannel) return;
    const targetChannelId = userProfile?.channelId || activeAcc?.channelId || null;
    fetchChannelCustomization(targetChannelId, profileHandle).then((data) => {
      if (data) {
        setChannelData(data);
        try {
          const acc = getActiveAccount();
          if (acc) {
            let changed = false;
            if (data.banner_url === '' && acc.banner_url) {
              acc.banner_url = '';
              changed = true;
            }
            if (data.avatar_url === '' && (acc.avatar_url || acc.profile_image)) {
              acc.avatar_url = '';
              acc.profile_image = '';
              changed = true;
            }
            if (changed) {
              localStorage.setItem('speedotube_user_account', JSON.stringify(acc));
              window.dispatchEvent(new CustomEvent('speedotube_user_switched', { detail: acc }));
            }
          }
        } catch (e) {}
      }
    });
  }, [userProfile, profileHandle, activeAcc]);

  const getJoinedDateString = () => {
    try {
      const dateRaw = userProfile?.createdAt || userProfile?.loginTime;
      const date = dateRaw ? new Date(dateRaw) : new Date();
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `Joined ${day} ${month} ${year}`;
    } catch (e) {
      const now = new Date();
      return `Joined ${now.getDate()} Sept ${now.getFullYear()}`;
    }
  };

  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploaded, setIsUploaded] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [isClosingAboutModal, setIsClosingAboutModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const fileInputRef = useRef(null);

  const handleOpenAboutModal = () => {
    setIsClosingAboutModal(false);
    setShowAboutModal(true);
  };

  const handleCloseAboutModal = () => {
    setIsClosingAboutModal(true);
    setTimeout(() => {
      setShowAboutModal(false);
      setIsClosingAboutModal(false);
    }, 240);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = (file) => {
    setSelectedFile(file);
    setIsUploaded(false);
    setUploadProgress(0);

    // Simulate upload progress animation
    let current = 0;
    const interval = setInterval(() => {
      current += 15;
      if (current >= 100) {
        setUploadProgress(100);
        setIsUploaded(true);
        clearInterval(interval);
      } else {
        setUploadProgress(current);
      }
    }, 200);
  };

  const handleResetUpload = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploaded(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleShareClick = () => {
    const link = `${window.location.origin}/channel/${profileHandle}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link);
    }
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const renderVideoCard = (vid) => {
    const chName = vid.channelName || vid.channel_name || profileName || 'Speedo Creator';
    const chHandle = vid.handleName || vid.handle_name || profileHandle || '';
    const chAvatar = vid.channelAvatar || vid.channel_avatar || channelAvatar || '';
    const preparedVideo = {
      ...vid,
      id: String(vid.id || vid.dbId || vid.video_url_id),
      videoUrlId: vid.video_url_id || vid.videoUrlId || String(vid.id || vid.dbId),
      title: vid.title || 'Untitled Video',
      channelName: chName,
      handleName: chHandle,
      channelAvatar: chAvatar,
      verified: vid.verified !== undefined ? Boolean(Number(vid.verified)) : true,
      views: vid.views !== undefined ? `${vid.views}` : '0',
      timestamp: vid.timestamp || formatRelativeTime(vid.created_date || vid.createdAt),
      duration: vid.duration ? (typeof vid.duration === 'string' && vid.duration.includes(':') ? vid.duration : formatDuration(vid.duration)) : '0:00',
      rawDuration: typeof vid.duration === 'number' ? vid.duration : parseInt(vid.duration, 10) || 0,
      thumbnailUrl: normalizeMediaUrl(vid.thumbnail_url || vid.thumbnailUrl) || DEFAULT_VIDEO_THUMBNAIL,
      video_url: vid.video_url ? normalizeMediaUrl(vid.video_url) : undefined,
      video_144_url: vid.video_144_url ? normalizeMediaUrl(vid.video_144_url) : undefined,
    };

    return (
      <VideoCard
        key={preparedVideo.id}
        video={preparedVideo}
        onVideoClick={(v) => {
          const vidId = v.videoUrlId || v.video_url_id || v.id;
          const watchPath = `/watch?v=${vidId}`;
          if (onNavigate) {
            onNavigate(watchPath);
          } else {
            window.history.pushState({}, '', watchPath);
            window.dispatchEvent(
              new CustomEvent('speedotube_navigate', {
                detail: { path: watchPath, videoId: vidId },
              })
            );
          }
        }}
        onChannelClick={(handle) => {
          if (handle) {
            const cleanHandle = handle.startsWith('@') ? handle : `@${handle}`;
            const channelPath = `/channel/${encodeURIComponent(cleanHandle)}`;
            if (onNavigate) {
              onNavigate(channelPath);
            } else {
              window.history.pushState({}, '', channelPath);
              window.dispatchEvent(
                new CustomEvent('speedotube_navigate', {
                  detail: { path: channelPath },
                })
              );
            }
          }
        }}
      />
    );
  };

  return (
    <div className="yt-channel-page" style={{ '--user-avatar-color': avatarColor }}>
      {/* Channel Hero Section with Banner as Backdrop Behind Content & Bottom Vignette */}
      <div className={`yt-channel-hero ${channelBanner ? 'has-banner' : ''} banner-theme-${bannerTheme}`}>
        {channelBanner && (
          <div className="yt-channel-banner-backdrop">
            <img
              src={channelBanner}
              alt="Channel Banner"
              className="yt-channel-banner-img"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className={`yt-channel-banner-scrim ${bannerTheme}`} />
            <div className="yt-channel-banner-vignette" />
          </div>
        )}

        {/* Channel Header Info Section */}
        <div className="yt-channel-header">
          <div className="yt-channel-avatar-large" style={{ backgroundColor: avatarColor, overflow: 'hidden' }}>
            {channelAvatar ? (
              <img
                src={channelAvatar}
                alt={profileName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              profileInitial
            )}
          </div>
          <div className="yt-channel-details">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h1 className="yt-channel-title" style={{ margin: 0 }}>{profileName}</h1>
              <MdCheckCircle size={20} className="yt-channel-verified-icon" style={{ flexShrink: 0 }} title="Verified" />
            </div>
            <div className="yt-channel-meta-line">
              <span className="yt-channel-handle">{profileHandle}</span>
              <span className="yt-channel-meta-dot">•</span>
              <span className="yt-channel-sub-count">{formatSubscribers(subscribersCount)}</span>
              <span className="yt-channel-meta-dot">•</span>
              <span className="yt-channel-vid-count">
                {channelVideos.length + channelShorts.length} {channelVideos.length + channelShorts.length === 1 ? 'video' : 'videos'}
              </span>
            </div>
            <div className="yt-channel-about">
              More about this channel{' '}
              <span
                className="yt-channel-more"
                onClick={handleOpenAboutModal}
                role="button"
                tabIndex={0}
              >
                ...more
              </span>
            </div>
            <div className="yt-channel-actions">
              {isOwnChannel ? (
                <>
                  <button
                    className="yt-channel-btn secondary"
                    onClick={() => { if (onNavigate) onNavigate('/studio/customization'); }}
                  >
                    Customise channel
                  </button>
                  <button
                    className="yt-channel-btn secondary"
                    onClick={() => { if (onNavigate) onNavigate('/studio/content'); }}
                  >
                    Manage videos
                  </button>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Join Button */}
                  <button className="yt-watch-join-btn">Join</button>

                  {/* Subscribe Button - Creative Animated Subscribe */}
                  <button
                    key={`sub-btn-${subAnimKey}`}
                    className={`yt-watch-subscribe-btn yt-sub-creative ${isSubscribed ? 'subscribed' : ''} ${
                      subAnimType === 'subscribing'
                        ? 'anim-subscribing'
                        : subAnimType === 'unsubscribing'
                        ? 'anim-unsubscribing'
                        : ''
                    }`}
                    onClick={handleSubscribeToggle}
                    aria-label={isSubscribed ? 'Subscribed to this channel' : 'Subscribe to this channel'}
                  >
                    {/* Creative Ambient Aura Glow */}
                    <span className="yt-sub-ambient-glow" aria-hidden="true" />
                    
                    {/* Shimmer light-beam sweep across pill */}
                    <span className="yt-sub-shimmer-sweep" aria-hidden="true" />

                    <span
                      className={`yt-sub-content-inner ${
                        isSubscribed ? 'is-subscribed' : 'is-unsubscribed'
                      } ${subAnimType ? 'anim-active' : ''}`}
                    >
                      {isSubscribed ? (
                        <>
                          <span className="yt-sub-icon-wrap yt-sub-bell-wrap">
                            <MdNotificationsActive size={17} className="yt-sub-bell-icon" />
                          </span>
                          <span className="yt-sub-text">Subscribed</span>
                        </>
                      ) : (
                        <span className="yt-sub-text">Subscribe</span>
                      )}
                    </span>

                    <span className="yt-sub-ripple-container">
                      {subAnimType === 'subscribing' && <span className="yt-sub-touch-ripple" />}
                    </span>

                    {/* Creative Sparkling Star Burst on Subscribing */}
                    {subAnimType === 'subscribing' && (
                      <span className="yt-sub-sparkle-burst" aria-hidden="true">
                        <span className="yt-sub-sparkle s1" />
                        <span className="yt-sub-sparkle s2" />
                        <span className="yt-sub-sparkle s3" />
                        <span className="yt-sub-sparkle s4" />
                        <span className="yt-sub-sparkle s5" />
                        <span className="yt-sub-sparkle s6" />
                        <span className="yt-sub-sparkle s7" />
                        <span className="yt-sub-sparkle s8" />
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Channel Navigation Tabs */}
      <div className="yt-channel-tabs-bar">
        <button
          type="button"
          className={`yt-channel-tab-btn ${activeTab === 'videos' ? 'active' : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          Videos {channelVideos.length > 0 ? `(${channelVideos.length})` : ''}
        </button>
        <button
          type="button"
          className={`yt-channel-tab-btn ${activeTab === 'shorts' ? 'active' : ''}`}
          onClick={() => setActiveTab('shorts')}
        >
          Shorts {channelShorts.length > 0 ? `(${channelShorts.length})` : ''}
        </button>
        {isOwnChannel && (
          <button
            type="button"
            className={`yt-channel-tab-btn ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            Upload
          </button>
        )}
        <button
          type="button"
          className={`yt-channel-tab-btn ${activeTab === 'about' ? 'active' : ''}`}
          onClick={handleOpenAboutModal}
        >
          About
        </button>
      </div>

      {/* Videos Tab Content (Exact 3 Videos Per Row layout as Home Page) */}
      {activeTab === 'videos' && (
        <div key="videos" className="yt-channel-tab-pane yt-channel-videos-wrapper">
          {channelVideos.length > 0 ? (
            <div className="yt-channel-videos-grid yt-video-grid">
              {channelVideos.map(renderVideoCard)}
            </div>
          ) : (
            <div className="yt-channel-empty-box">
              <MdSmartDisplay size={56} className="yt-channel-empty-icon" />
              <h3 className="yt-channel-empty-title">This channel has no videos yet</h3>
              <p className="yt-channel-empty-sub">
                {isOwnChannel
                  ? "Upload your first video to start growing your channel!"
                  : "When this creator uploads videos, they'll appear here."}
              </p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {channelShorts.length > 0 && (
                  <button
                    className="yt-upload-primary-btn"
                    style={{ backgroundColor: avatarColor }}
                    onClick={() => setActiveTab('shorts')}
                  >
                    <MdSmartDisplay size={18} />
                    <span>View Shorts ({channelShorts.length})</span>
                  </button>
                )}
                {isOwnChannel && (
                  <button
                    className="yt-channel-btn secondary"
                    onClick={() => setActiveTab('upload')}
                  >
                    <MdFileUpload size={18} />
                    <span>Upload a video</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Shorts Tab Content */}
      {activeTab === 'shorts' && (
        <div key="shorts" className="yt-channel-tab-pane yt-channel-videos-wrapper">
          {channelShorts.length > 0 ? (
            <div className="yt-channel-shorts-grid">
              {channelShorts.map((short) => {
                const rawThumb = short.thumbnail_url || (short.thumbnail_destination ? `${API_BASE_URL}${short.thumbnail_destination}` : null) || `${process.env.PUBLIC_URL || ''}/img/banner.png`;
                const shortThumb = normalizeMediaUrl(rawThumb);
                return (
                  <div
                    key={short.id || short.dbId}
                    className="yt-channel-short-card"
                    onClick={() => {
                      const shortId = short.video_url_id || short.videoUrlId || short.id || short.dbId;
                      const watchPath = `/shorts/${shortId}`;
                      if (onNavigate) {
                        onNavigate(watchPath);
                      } else {
                        window.history.pushState({}, '', watchPath);
                        window.dispatchEvent(
                          new CustomEvent('speedotube_navigate', {
                            detail: { path: watchPath, videoId: shortId },
                          })
                        );
                      }
                    }}
                  >
                    <div className="yt-channel-short-thumb-box">
                      <img
                        src={shortThumb}
                        alt={short.title}
                        className="yt-channel-short-thumb-img"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `${process.env.PUBLIC_URL || ''}/img/banner.png`;
                        }}
                      />
                      <span className="yt-channel-short-duration">
                        {formatDuration(short.duration)}
                      </span>
                      <div className="yt-channel-short-play-overlay">
                        <MdSmartDisplay size={36} color="#ffffff" />
                      </div>
                    </div>
                    <div className="yt-channel-short-info">
                      <h3 className="yt-channel-short-title" title={short.title}>
                        {short.title}
                      </h3>
                      <div className="yt-channel-short-views">
                        {short.views || 0} views
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="yt-channel-empty-box">
              <MdSmartDisplay size={56} className="yt-channel-empty-icon" />
              <h3 className="yt-channel-empty-title">This channel has no shorts yet</h3>
              <p className="yt-channel-empty-sub">
                {isOwnChannel
                  ? "Upload your first Short to start growing your channel!"
                  : "When this creator uploads shorts, they'll appear here."}
              </p>
              {isOwnChannel && (
                <button
                  className="yt-upload-primary-btn"
                  style={{ marginTop: '14px', backgroundColor: avatarColor }}
                  onClick={() => setActiveTab('upload')}
                >
                  <MdFileUpload size={18} />
                  <span>Upload a short</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        accept="video/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Interactive Drag and Drop Upload Zone (only when on upload tab for own channel) */}
      {isOwnChannel && activeTab === 'upload' && (
      <div key="upload" className="yt-channel-tab-pane yt-channel-upload-section">
        {!selectedFile ? (
          <div
            className={`yt-upload-dropzone ${isDragging ? 'is-dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            {/* Animated Cloud Icon with Continuous Upward Flowing Arrow */}
            <div className="yt-upload-icon-circle" style={{ backgroundColor: `${avatarColor}26`, color: avatarColor }}>
              <svg
                width="54"
                height="54"
                viewBox="0 0 24 24"
                className="yt-upload-cloud-svg"
              >
                <defs>
                  <clipPath id="cloud-inner-clip">
                    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04Z" />
                  </clipPath>
                </defs>
                <path
                  d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04Z"
                  fill="currentColor"
                />
                <g clipPath="url(#cloud-inner-clip)">
                  <path
                    className="yt-upload-continuous-arrow"
                    d="M12 7.5L7.5 12H10.5V16.5H13.5V12H16.5L12 7.5Z"
                    fill="#ffffff"
                  />
                </g>
              </svg>
            </div>

            <h2 className="yt-upload-title">Drag and drop video files to upload</h2>
            <p className="yt-upload-subtitle">
              Your videos will remain private until you choose to publish them.
            </p>

            <div className="yt-upload-btn-group" onClick={(e) => e.stopPropagation()}>
              <button
                className="yt-upload-primary-btn"
                style={{ backgroundColor: avatarColor, boxShadow: `0 4px 14px ${avatarColor}4d` }}
                onClick={() => {
                  if (onOpenUpload) {
                    onOpenUpload();
                  } else {
                    // Fallback: open file picker directly if modal handler not available
                    fileInputRef.current && fileInputRef.current.click();
                  }
                }}
              >
                <MdFileUpload size={20} />
                <span>Upload Content</span>
              </button>
            </div>

            {/* Supported Formats & Feature Badges */}
            <div className="yt-upload-format-badges">
              <span className="yt-format-badge">MP4</span>
              <span className="yt-format-badge">MOV</span>
              <span className="yt-format-badge">WEBM</span>
              <span className="yt-format-badge">AVI</span>
              <span className="yt-format-badge resolution" style={{ backgroundColor: `${avatarColor}26`, color: avatarColor }}>Up to 4K Ultra HD</span>
            </div>
          </div>
        ) : (
          /* File Selected Upload Progress Card */
          <div className="yt-upload-preview-card">
            <div className="yt-upload-card-header">
              <div className="yt-file-icon-box" style={{ backgroundColor: `${avatarColor}26`, color: avatarColor }}>
                <MdOutlineVideoLibrary size={32} />
              </div>
              <div className="yt-file-info">
                <div className="yt-file-name">{selectedFile.name}</div>
                <div className="yt-file-size">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB • {isUploaded ? 'Upload Complete' : 'Uploading...'}
                </div>
              </div>
              <button className="yt-file-cancel-btn" onClick={handleResetUpload} title="Cancel">
                <MdClose size={20} />
              </button>
            </div>

            {/* Upload Progress Bar */}
            <div className="yt-upload-progress-container">
              <div
                className={`yt-upload-progress-bar ${isUploaded ? 'completed' : ''}`}
                style={{ width: `${uploadProgress}%` }}
              />
            </div>

            {/* Status Footer */}
            <div className="yt-upload-card-footer">
              {isUploaded ? (
                <div className="yt-upload-success-status">
                  <MdCheckCircle size={20} className="yt-success-icon" />
                  <span>Video processed successfully! Ready to publish.</span>
                </div>
              ) : (
                <div className="yt-upload-status-text">
                  Uploading video... {uploadProgress}%
                </div>
              )}

              {isUploaded && (
                <div className="yt-upload-card-actions">
                  <button className="yt-channel-btn secondary" onClick={handleResetUpload}>
                    Upload another
                  </button>
                  <button className="yt-upload-primary-btn publish">
                    Publish Video
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* --- About Channel Modal --- */}
      {showAboutModal && (
        <div
          className={`yt-about-modal-backdrop ${isClosingAboutModal ? 'exiting' : ''}`}
          onClick={handleCloseAboutModal}
        >
          <div
            className={`yt-about-modal-card ${isClosingAboutModal ? 'exiting' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="yt-about-modal-header">
              <h2 className="yt-about-modal-channel-name">{profileName}</h2>
              <button
                type="button"
                className="yt-about-close-btn"
                onClick={handleCloseAboutModal}
                title="Close"
              >
                <MdClose size={20} />
              </button>
            </div>

            <div className="yt-about-modal-content">
              {channelData?.description && (
                <div style={{ marginBottom: '16px', color: '#e2e8f0', fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {channelData.description}
                </div>
              )}

              <h3 className="yt-about-section-title">More info</h3>

              <div className="yt-about-info-rows">
                <div className="yt-about-info-row">
                  <MdOutlineInfo size={22} className="yt-about-info-icon" />
                  <span className="yt-about-info-text">{getJoinedDateString()}</span>
                </div>
                {channelData?.email && (
                  <div className="yt-about-info-row">
                    <span className="yt-about-info-text" style={{ color: '#aaaaaa', fontSize: '13px' }}>
                      Business Inquiries: <a href={`mailto:${channelData.email}`} style={{ color: '#38bdf8' }}>{channelData.email}</a>
                    </span>
                  </div>
                )}
              </div>

              {channelData?.custom_links && channelData.custom_links.length > 0 && (
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '13px', color: '#aaaaaa', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Links</h4>
                  {channelData.custom_links.map((link, idx) => (
                    <div key={idx} style={{ marginBottom: '6px' }}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#38bdf8', fontSize: '13px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        🔗 {link.title}
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {/* Share Action Row: Share button has ONLY the Share Icon */}
              <div className="yt-about-actions-row">
                <button
                  type="button"
                  className="yt-about-share-icon-btn"
                  title="Share channel"
                  onClick={handleShareClick}
                >
                  <RiShareForwardFill size={22} />
                </button>
                {copiedLink && <span className="yt-copied-toast">Link copied!</span>}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Sign-In Prompt Modal for unauthenticated subscription attempt */}
      <SignInPromptModal
        isOpen={signInPrompt.isOpen}
        onClose={() => setSignInPrompt({ isOpen: false, action: 'general', targetName: '' })}
        onSignIn={() => {
          setSignInPrompt({ isOpen: false, action: 'general', targetName: '' });
          if (onLogin) {
            onLogin();
          } else if (onNavigate) {
            onNavigate('/auth');
          } else {
            window.history.pushState({}, '', '/auth');
            window.dispatchEvent(new CustomEvent('speedotube_navigate', { detail: { path: '/auth' } }));
          }
        }}
        action={signInPrompt.action}
        targetName={signInPrompt.targetName}
      />
    </div>
  );
}
